import { useRouter } from 'next/router';
import languageMessagesConstants from '@/constants/language.translations.constants';
import { getValidLocale } from '@/utilites/language/language.utils';
import { useEffect, useState } from 'react';
import useBackendUser from '@/hooks/useBackendUser';

function OneSignalComponent() {
  const { locale = 'en' } = useRouter();
  const [oneSignalInitialized, setOneSignalInitialized] = useState(false);
  const [serviceWorkerRegistered, setServiceWorkerRegistered] = useState(false);
  const { data: user = null, isLoading: isLoadingUser, isError: isErrorUser } = useBackendUser();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      const onLoad = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration(
            '/push/onesignal/OneSignalSDKWorker.js'
          );

          if (!registration) {
            await navigator.serviceWorker.register('/push/onesignal/OneSignalSDKWorker.js');
          }
          setServiceWorkerRegistered(true);
        } catch (err) {
          console.error(err);
        }
      };

      onLoad();
    }
  }, []);

  useEffect(() => {
    const runOneSignal = async () => {
      const OneSignal = (await import('react-onesignal')).default;
      const rightLocale = getValidLocale(locale);
      const oneSignalInitMessages = languageMessagesConstants.oneSignalInit[`${rightLocale}`];
      const {
        title: actionMessage,
        allow: acceptButton,
        cancel: cancelButton,
      } = oneSignalInitMessages;
      if (!oneSignalInitialized) {
        setOneSignalInitialized(true);
        try {
          await OneSignal.init({
            appId: process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID!,
            promptOptions: {
              slidedown: {
                prompts: [
                  {
                    type: 'push',
                    autoPrompt: true,
                    categories: [],
                    text: {
                      actionMessage,
                      acceptButton,
                      cancelMessage: cancelButton,
                    },
                    delay: {
                      pageViews: 1,
                      timeDelay: 20,
                    },
                  },
                ],
              },
            },
            allowLocalhostAsSecureOrigin: true,
            serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
            serviceWorkerParam: { scope: '/push/onesignal/' },
          });
        } catch (e) {
          console.error(e);
        }
      }
    };

    if (!oneSignalInitialized && serviceWorkerRegistered) {
      runOneSignal();
    }
  }, [locale, oneSignalInitialized, serviceWorkerRegistered]);

  useEffect(() => {
    async function runOneSignal() {
      const OneSignal = (await import('react-onesignal')).default;
      if (oneSignalInitialized && serviceWorkerRegistered && !isLoadingUser) {
        if (user && user.email) {
          OneSignal.login(user.email);
        } else if (!OneSignal.User.PushSubscription.id) {
          OneSignal.Slidedown.promptPush();
        }
      }
    }
    runOneSignal();
  }, [user, isLoadingUser, isErrorUser, oneSignalInitialized, serviceWorkerRegistered]);

  return null;
}

export default OneSignalComponent;
